import { z } from 'zod';

const base = { id: z.string().regex(/^[a-z0-9_-]{1,128}$/), tUs: z.number().int().nonnegative().max(1_000_000_000) };
const norm = z.number().min(0).max(1), byte = z.number().int().min(0).max(255), axis = z.number().int().min(-32768).max(32767);
export const macroInputSelfTestSampleSchema = z.discriminatedUnion('kind', [
  z.object({ ...base, kind: z.literal('key'), vk: z.number().int().min(1).max(254), scan: z.number().int().min(0).max(65535),
    extended: z.boolean(), action: z.enum(['down', 'up']) }).strict(),
  z.object({ ...base, kind: z.literal('mouse-rel'), dx: z.number().int(), dy: z.number().int() }).strict(),
  z.object({ ...base, kind: z.literal('mouse-abs'), x: norm, y: norm }).strict(),
  z.object({ ...base, kind: z.literal('mouse-button'), button: z.enum(['left', 'right', 'middle', 'back', 'forward']),
    action: z.enum(['down', 'up']), x: norm, y: norm }).strict(),
  z.object({ ...base, kind: z.literal('mouse-wheel'), dx: z.number().int().min(-32767).max(32767), dy: z.number().int().min(-32767).max(32767) }).strict(),
  z.object({ ...base, kind: z.literal('touch'), contactId: z.number().int().min(0).max(9), action: z.enum(['down', 'move', 'up']), x: norm, y: norm }).strict(),
  z.object({ ...base, kind: z.literal('gamepad'), slot: z.number().int().min(0).max(3), buttons: z.number().int().min(0).max(65535),
    lt: byte, rt: byte, lx: axis, ly: axis, rx: axis, ry: axis }).strict(),
]);
export type MacroInputSelfTestSample = z.infer<typeof macroInputSelfTestSampleSchema>;
const key = (id: string, tUs: number, vk: number, scan: number, action: 'down' | 'up'): MacroInputSelfTestSample =>
  ({ id, tUs, kind: 'key', vk, scan, extended: false, action });

/** Fixed source stimulus, never fabricated recorded macro rows or receiver observations. */
export const MACRO_INPUT_SELF_TEST_RECIPE: readonly MacroInputSelfTestSample[] = Object.freeze([
  ...([0, 1, 2, 3] as const).flatMap(slot => [
    { id: `gamepad-${slot}-active`, tUs: 500_000 + slot * 600_000, kind: 'gamepad' as const, slot,
      buttons: 0x1000 | (1 << slot), lt: 48 + slot * 16, rt: 192 - slot * 16, lx: -12000, ly: 16000, rx: 22000, ry: -8000 },
    { id: `gamepad-${slot}-neutral`, tUs: 800_000 + slot * 600_000, kind: 'gamepad' as const, slot,
      buttons: 0, lt: 0, rt: 0, lx: 0, ly: 0, rx: 0, ry: 0 },
  ]),
  key('key-a-down', 3_000_000, 65, 30, 'down'), key('key-a-up', 3_200_000, 65, 30, 'up'),
  key('shift-down', 3_500_000, 16, 42, 'down'), key('chord-b-down', 3_600_000, 66, 48, 'down'),
  key('chord-b-up', 3_750_000, 66, 48, 'up'), key('shift-up', 3_850_000, 16, 42, 'up'),
  key('held-a-down', 4_100_000, 65, 30, 'down'), key('held-a-up', 4_600_000, 65, 30, 'up'),
  { id: 'absolute-start', tUs: 4_900_000, kind: 'mouse-abs', x: .35, y: .4 },
  { id: 'relative-out', tUs: 5_200_000, kind: 'mouse-rel', dx: 24, dy: -12 },
  { id: 'relative-back', tUs: 5_500_000, kind: 'mouse-rel', dx: -24, dy: 12 },
  { id: 'absolute-finish', tUs: 5_800_000, kind: 'mouse-abs', x: .65, y: .6 },
  ...(['left', 'right', 'middle', 'back', 'forward'] as const).flatMap((button, index) => [
    { id: `${button}-down`, tUs: 6_100_000 + index * 500_000, kind: 'mouse-button' as const, button, action: 'down' as const, x: .65, y: .6 },
    { id: `${button}-up`, tUs: 6_300_000 + index * 500_000, kind: 'mouse-button' as const, button, action: 'up' as const, x: .65, y: .6 },
  ]),
  { id: 'wheel-down', tUs: 8_600_000, kind: 'mouse-wheel', dx: 0, dy: 120 },
  { id: 'wheel-up', tUs: 8_900_000, kind: 'mouse-wheel', dx: 0, dy: -120 },
  { id: 'wheel-right', tUs: 9_200_000, kind: 'mouse-wheel', dx: 120, dy: 0 },
  { id: 'wheel-left', tUs: 9_500_000, kind: 'mouse-wheel', dx: -120, dy: 0 },
]);
export const MACRO_INPUT_SELF_TEST_DURATION_US = 10_000_000;
export const MACRO_INPUT_SELF_TEST_RELATIVE_RANGE_US = Object.freeze({ fromUs: 5_050_000, toUs: 5_650_000 });

export const macroInputSelfTestRecipeSchema = z.array(macroInputSelfTestSampleSchema).length(MACRO_INPUT_SELF_TEST_RECIPE.length)
  .superRefine((recipe, context) => {
    if (JSON.stringify(recipe) !== JSON.stringify(MACRO_INPUT_SELF_TEST_RECIPE)) context.addIssue({ code: 'custom', message: 'The canonical sample recipe cannot be changed.' });
  });

export function macroInputSelfTestDevice(sample: MacroInputSelfTestSample): 'keyboard' | 'mouse' | 'touch' | 'gamepad0' | 'gamepad1' | 'gamepad2' | 'gamepad3' {
  return sample.kind === 'key' ? 'keyboard' : sample.kind === 'gamepad' ? (['gamepad0', 'gamepad1', 'gamepad2', 'gamepad3'] as const)[sample.slot]
    : sample.kind === 'touch' ? 'touch' : 'mouse';
}
